// Backwards-planning engine for recurring district events.
//
// Pure logic, zero Firebase. The mechanic, from the district DEs' "Backdater"
// workbook: every prep step carries an offset in days relative to the event
// ("Days Before/After": -140 … 0 … +7). Set the event date once; each step's
// due date is `eventDate + offsetDays`. Monthly things (Roundtable, District
// Committee Meeting) repeat the same offsets against several dates.
//
// Model types (EventTemplate, EventTemplateStep, Initiative) live in
// src/types.ts; this module is just the date math + generation.

import type { DefaultTaskKind, EventTemplate, Initiative, ISODateString } from '../types';

/** The date-math in this module never reads `tenantId` — accept a template
 *  with or without it so pure/seed-data callers (no live tenant context)
 *  don't need to fabricate one. */
type PlanTemplate = Omit<EventTemplate, 'tenantId'>;

/** One generated, not-yet-persisted task. A real Task is derived from this by
 *  attaching districtId + initiativeId + source at persist time. */
export interface PlannedTask {
  title: string;
  category?: string;
  offsetDays: number;
  dueDate: ISODateString;
  assignedPosition?: string;
  notes?: string;
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

/** Add (or subtract) whole days to an ISO date, UTC-based to avoid DST drift. */
export function addDays(date: ISODateString, days: number): ISODateString {
  if (!ISO_RE.test(date)) {
    throw new Error(`addDays: expected YYYY-MM-DD, got ${JSON.stringify(date)}`);
  }
  const [y, m, d] = date.split('-').map(Number);
  const shifted = new Date(Date.UTC(y, m - 1, d) + days * DAY_MS);
  return shifted.toISOString().slice(0, 10);
}

function planFor(
  template: PlanTemplate,
  eventDate: ISODateString,
): PlannedTask[] {
  return template.steps.map((step) => ({
    title: step.title,
    category: step.category,
    offsetDays: step.offsetDays,
    dueDate: addDays(eventDate, step.offsetDays),
    assignedPosition: step.defaultPosition,
    notes: step.notes,
  }));
}

function byDate(a: PlannedTask, b: PlannedTask): number {
  return a.dueDate.localeCompare(b.dueDate) || a.offsetDays - b.offsetDays;
}

/** Generate the dated task list for a one-off event. */
export function generatePlan(
  template: PlanTemplate,
  eventDate: ISODateString,
): PlannedTask[] {
  if (template.recurrence !== 'once') {
    throw new Error(
      `generatePlan: template "${template.name}" is ${template.recurrence}; use generateMonthlyPlan`,
    );
  }
  return planFor(template, eventDate).sort(byDate);
}

/** Generate one combined task list across several occurrence dates (monthly
 *  meetings). Each date contributes the full offset set. */
export function generateMonthlyPlan(
  template: PlanTemplate,
  eventDates: ISODateString[],
): PlannedTask[] {
  if (template.recurrence !== 'monthly') {
    throw new Error(
      `generateMonthlyPlan: template "${template.name}" is ${template.recurrence}; use generatePlan`,
    );
  }
  return eventDates.flatMap((d) => planFor(template, d)).sort(byDate);
}

/** Earliest step offset (<= 0) — how far ahead a district must start once it
 *  sets a date. */
export function planLeadTimeDays(template: PlanTemplate): number {
  return Math.min(0, ...template.steps.map((s) => s.offsetDays));
}

/** Generate a plan from a template regardless of recurrence — the one entry
 *  point the UI needs. For a monthly template, `eventDate` is the first
 *  occurrence and `extraDates` the rest. */
export function planFromTemplate(
  template: PlanTemplate,
  eventDate: ISODateString,
  extraDates: ISODateString[] = [],
): PlannedTask[] {
  return template.recurrence === 'monthly'
    ? generateMonthlyPlan(template, [eventDate, ...extraDates])
    : generatePlan(template, eventDate);
}

/** Template steps as task drafts with no due date yet — used when an initiative
 *  is created before its date is known. Due dates are filled in later by
 *  `dueDatesFromEventDate` once the Date task sets `eventDate`. */
export interface StepDraft {
  title: string;
  category?: string;
  offsetDays: number;
  assignedPosition?: string;
  description: string;
}

export function stepDrafts(template: PlanTemplate): StepDraft[] {
  return template.steps.map((s) => ({
    title: s.title,
    category: s.category,
    offsetDays: s.offsetDays,
    assignedPosition: s.defaultPosition,
    description: s.notes ?? '',
  }));
}

/** Given the now-known event date, the due date for every task that carries an
 *  offset. Returns only the tasks it can date. */
export function dueDatesFromEventDate(
  tasks: Array<{ id: string; offsetDays?: number | null }>,
  eventDate: ISODateString,
): Array<{ id: string; dueDate: ISODateString }> {
  return tasks
    .filter((t) => typeof t.offsetDays === 'number')
    .map((t) => ({ id: t.id, dueDate: addDays(eventDate, t.offsetDays as number) }));
}

// ---- the four built-in tasks -------------------------------------------

export interface DefaultTaskDef {
  kind: DefaultTaskKind;
  title: string;
  description: string;
  category: string;
}

/** Offered on every new initiative, checked by default. Order is display order. */
export const DEFAULT_TASKS: DefaultTaskDef[] = [
  {
    kind: 'date',
    title: 'Set the date',
    description:
      'Agree the event date (or dates), decide all-day vs. specific times, and set a backup date for weather. Record it on the initiative — that unlocks the rest of the schedule.',
    category: 'Setup',
  },
  {
    kind: 'location',
    title: 'Secure the location',
    description:
      'Find a venue, get it approved and reserved, then record the final location name and address on the initiative.',
    category: 'Setup',
  },
  {
    kind: 'registration',
    title: 'Set up registration',
    description:
      'Build the registration page and get it ready to open, then record the link on the initiative.',
    category: 'Setup',
  },
  {
    kind: 'eventChair',
    title: 'Recruit the event chair',
    description:
      'Identify and confirm who will chair this event, then record them on the initiative.',
    category: 'Setup',
  },
];

/** Is the Initiative field behind a built-in task filled in? Drives whether the
 *  task may be marked complete. */
export function initiativeFieldReady(kind: DefaultTaskKind, i: Initiative): boolean {
  switch (kind) {
    case 'date':
      return !!i.eventDate;
    case 'location':
      return !!i.locationName && !!i.locationAddress;
    case 'registration':
      return !!i.registrationUrl;
    case 'eventChair':
      return !!i.leadPosition || (i.leadContactIds?.length ?? 0) > 0 || (i.leadPersonIds?.length ?? 0) > 0;
  }
}
